import { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, gbp, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtNum, resolveUnits, qtyOf, qtyText, levelsText, unitWord } from "../lib/purchaseUnits";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const label = (p) => `${MONTHS[Number(p.slice(5, 7)) - 1]} ${p.slice(2, 4)}`;

function Kpi({ title, main, sub, tone, testId }) {
  return (
    <div className={`rounded-lg p-3 ${tone === "primary" ? "bg-emerald-600 text-white" : "bg-slate-50 border border-slate-200"}`} data-testid={testId}>
      <p className={`text-[10px] uppercase tracking-wider font-semibold ${tone === "primary" ? "text-emerald-100" : "text-slate-500"}`}>{title}</p>
      <p className={`font-mono text-lg font-bold ${tone === "primary" ? "" : "text-slate-900"}`}>{main}</p>
      <p className={`text-[11px] ${tone === "primary" ? "text-emerald-100" : "text-slate-500"}`}>{sub}</p>
    </div>
  );
}

const withLevels = (o, units, unit) => { const l = levelsText(o, units, unit); return l ? ` · ${l}` : ""; };

export default function PurchaseHistory({ productId }) {
  const { restaurantId } = useAuth();
  const [data, setData] = useState(null);
  const [year, setYear] = useState(null);
  const [metric, setMetric] = useState("spend");

  useEffect(() => {
    api.get(`/supplier-products/${productId}/purchases`, { params: { restaurant_id: restaurantId || undefined } })
      .then((r) => { setData(r.data); setYear(r.data?.years?.[0]?.year || null); }).catch((e) => toast.error(errorMessage(e)));
  }, [productId, restaurantId]);

  const units = useMemo(() => (data ? resolveUnits(data) : []), [data]);
  const years = useMemo(() => data?.years || [], [data]);
  const y = useMemo(() => years.find((x) => x.year === year) || years[0], [years, year]);
  const isQty = metric !== "spend";
  const unit = units.find((u) => u.key === metric) || units[0]; // a stale metric (unit no longer offered) falls back to the first unit
  const rows = useMemo(() => {
    if (!y) return [];
    const byPeriod = Object.fromEntries((y.months || []).map((m) => [m.period, m]));
    return Array.from({ length: 12 }, (_, i) => {
      const p = `${y.year}-${String(i + 1).padStart(2, "0")}`;
      const m = byPeriod[p] || null; // months without purchases have no record — every consumer below must cope with null
      return { period: p, name: label(p), spend: Number(m?.spend) || 0, invoices: Number(m?.invoices) || 0, suppliers: m?.suppliers || {}, m, value: isQty ? qtyOf(m, unit) : Number(m?.spend) || 0 };
    });
  }, [y, isQty, unit]);

  if (!data) return <p className="text-sm text-slate-500">Loading purchases…</p>;
  if (!y) return <p className="text-sm text-slate-500 bg-white border border-slate-200 rounded-lg p-4" data-testid="purchases-empty">No confirmed purchases yet for this product in the selected restaurant.</p>;

  const qty = (o) => qtyText(o, unit);
  const activeMonths = (y.months || []).length || 1;
  const allTime = data.all_time || {};
  const tooltip = (v, _n, item) => {
    const m = item?.payload?.m;
    const spend = gbp(item?.payload?.spend || 0);
    const inv = item?.payload?.invoices || 0;
    const sups = Object.entries(item?.payload?.suppliers || {});
    const main = isQty ? `${qty(m)}${withLevels(m, units, unit)} · ${spend}` : `${gbp(v || 0)} · ${qty(m)}`;
    return [main, `${inv} invoice${inv === 1 ? "" : "s"}${sups.length ? " · " + sups.map(([s, a]) => `${s} ${gbp(a)}`).join(", ") : ""}`];
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 overflow-hidden" data-testid="purchase-history">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><ShoppingBag className="h-4 w-4 text-emerald-600" /> How much you buy</h2>
          <p className="text-xs text-slate-500">Confirmed invoices, month by month{data.group_size > 1 ? ` · combines ${data.group_size} merged names / equivalent products` : ""}{data.hierarchy?.summary ? ` · ${data.hierarchy.summary}` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="purchases-year-tabs">{years.map((yy) => <button key={yy.year} onClick={() => setYear(yy.year)} className={`px-3 py-1 rounded-md text-xs font-semibold ${yy.year === y.year ? "bg-white shadow text-slate-900" : "text-slate-500"}`} data-testid={`purchases-year-${yy.year}`}>{yy.year}</button>)}</div>
          <div className="flex flex-wrap gap-1 bg-slate-100 rounded-lg p-1" data-testid="purchases-metric">
            <button onClick={() => setMetric("spend")} className={`px-3 py-1 rounded-md text-xs font-semibold ${!isQty ? "bg-white shadow text-slate-900" : "text-slate-500"}`} data-testid="purchases-metric-spend">£ Spend</button>
            {units.map((u) => <button key={u.key} onClick={() => setMetric(u.key)} className={`px-3 py-1 rounded-md text-xs font-semibold ${isQty && unit?.key === u.key ? "bg-white shadow text-slate-900" : "text-slate-500"}`} data-testid={`purchases-metric-${u.key.replace(/^__/, "")}`}>{u.label}</button>)}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <Kpi tone="primary" testId="purchases-year-total" title={`${y.year} total`}
          main={isQty ? qty(y) : gbp(y.spend || 0)} sub={`${isQty ? gbp(y.spend || 0) : qty(y)}${withLevels(y, units, unit)} · ${y.invoices || 0} invoice${y.invoices === 1 ? "" : "s"}`} />
        <Kpi testId="purchases-year-avg" title="Avg per month"
          main={isQty ? `${fmtNum(qtyOf(y, unit) / activeMonths)} ${unitWord(unit, 2)}` : gbp(y.avg_monthly_spend || 0)} sub={`${activeMonths} active month${activeMonths === 1 ? "" : "s"}`} />
        <Kpi testId="purchases-all-time" title="All time"
          main={isQty ? qty(allTime) : gbp(allTime.spend || 0)} sub={`${isQty ? gbp(allTime.spend || 0) : qty(allTime)}${withLevels(allTime, units, unit)}`} />
      </div>
      <div className="h-56 -ml-2">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} />
            <YAxis tickFormatter={(v) => (isQty ? fmtNum(v) : `£${v}`)} tick={{ fontSize: 11, fill: "#64748b" }} width={56} allowDecimals={false} />
            <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={tooltip} />
            <Bar dataKey="value" fill="#059669" radius={[4, 4, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <ul className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5" data-testid="purchases-month-list">
        {(y.months || []).slice().reverse().map((m) => (
          <li key={m.period} className="flex items-baseline justify-between rounded-md bg-slate-50 px-2.5 py-1.5 text-xs" data-testid={`purchases-month-${m.period}`}>
            <span className="text-slate-600">{label(m.period)}</span>
            <span className="font-mono text-slate-900">{isQty ? qty(m) : gbp(m.spend || 0)} <span className="text-slate-400">· {isQty ? gbp(m.spend || 0) : qty(m)}</span></span>
          </li>
        ))}
      </ul>
    </div>
  );
}
